/**
 * AddRageRoom
 * -----------
 * Dedicated form page that lets authenticated admins create new rage rooms.
 * Captures the core metadata required by the backend (name, city, amenities,
 * capacity, availability window, etc.) and persists it through the admin API.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';

import useApi from '../hooks/useApi';

const defaultFormState = {
  name: '',
  city: '',
  locationName: '',
  description: '',
  capacity: '4',
  defaultSessionLengthMinutes: '30',
  availabilityStart: '',
  availabilityEnd: '',
  amenitiesInput: '',
  isActive: true,
};

const AddRageRoom = () => {
  const navigate = useNavigate();
  const { rageRoom } = useApi();
  const locationId = process.env.REACT_APP_DEFAULT_LOCATION_ID || 'default-location';

  const [formState, setFormState] = useState(defaultFormState);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleToggleActive = (event) => {
    setFormState((prev) => ({
      ...prev,
      isActive: event.target.checked,
    }));
  };

  const resetForm = () => {
    setFormState(defaultFormState);
  };

  const parseAmenities = (value) =>
    value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formState.name.trim() || !formState.city.trim() || !formState.locationName.trim()) {
      setErrorMessage('Name, City, and Location Name are required.');
      return;
    }

    const capacity = Number(formState.capacity);
    const sessionLength = Number(formState.defaultSessionLengthMinutes);
    const hasAvailabilityWindow = formState.availabilityStart && formState.availabilityEnd;

    const payload = {
      name: formState.name.trim(),
      city: formState.city.trim(),
      locationName: formState.locationName.trim(),
      description: formState.description.trim(),
      capacity: Number.isNaN(capacity) ? 4 : capacity,
      amenities: parseAmenities(formState.amenitiesInput),
      defaultSessionLengthMinutes: Number.isNaN(sessionLength) ? 30 : sessionLength,
      isActive: formState.isActive,
    };

    if (hasAvailabilityWindow) {
      payload.availabilityWindow = {
        startTime: formState.availabilityStart,
        endTime: formState.availabilityEnd,
      };
    }

    setSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      await rageRoom.admin.createRoom({
        locationId,
        payload,
      });
      setSuccessMessage('Rage room created successfully.');
      resetForm();

      setTimeout(() => {
        navigate('/admin/rooms');
      }, 1000);
    } catch (error) {
      setErrorMessage(error.message || 'Unable to create rage room.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack spacing={4}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Stack spacing={1}>
          <Typography variant="h4">Add Rage Room</Typography>
          <Typography color="text.secondary">
            Capture the baseline specs for a new rage room. You can import schedules or attach playlists later.
          </Typography>
        </Stack>
        <Button variant="text" onClick={() => navigate(-1)}>
          Back
        </Button>
      </Stack>

      <Card component="form" onSubmit={handleSubmit}>
        <CardContent>
          <Stack spacing={3}>
            {errorMessage && (
              <Alert severity="error" onClose={() => setErrorMessage('')}>
                {errorMessage}
              </Alert>
            )}
            {successMessage && (
              <Alert severity="success" onClose={() => setSuccessMessage('')}>
                {successMessage}
              </Alert>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Room Name"
                  name="name"
                  value={formState.name}
                  onChange={handleFieldChange}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="City"
                  name="city"
                  value={formState.city}
                  onChange={handleFieldChange}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Location Name"
                  name="locationName"
                  value={formState.locationName}
                  onChange={handleFieldChange}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  name="description"
                  value={formState.description}
                  onChange={handleFieldChange}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Capacity"
                  name="capacity"
                  type="number"
                  inputProps={{ min: 1 }}
                  value={formState.capacity}
                  onChange={handleFieldChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Default Session Length (minutes)"
                  name="defaultSessionLengthMinutes"
                  type="number"
                  inputProps={{ min: 15, max: 240 }}
                  value={formState.defaultSessionLengthMinutes}
                  onChange={handleFieldChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Availability Start"
                  name="availabilityStart"
                  type="time"
                  value={formState.availabilityStart}
                  onChange={handleFieldChange}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Availability End"
                  name="availabilityEnd"
                  type="time"
                  value={formState.availabilityEnd}
                  onChange={handleFieldChange}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Amenities"
                  name="amenitiesInput"
                  value={formState.amenitiesInput}
                  onChange={handleFieldChange}
                  helperText="Separate amenities with commas (e.g., Bluetooth speakers, Cooling towels)"
                  fullWidth
                />
              </Grid>
            </Grid>

            <Divider />

            <FormControlLabel
              control={<Switch checked={formState.isActive} onChange={handleToggleActive} />}
              label="Room is active"
            />

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={resetForm} disabled={submitting}>
                Reset
              </Button>
              <Button variant="contained" type="submit" disabled={submitting}>
                {submitting ? 'Saving…' : 'Create Rage Room'}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default AddRageRoom;